import { Tab, TabGroup, type TabGroupWC } from '@pypestream/design-system';
import React, {
  ElementRef,
  FC,
  ReactNode,
  RefObject,
  useLayoutEffect,
  useRef,
} from 'react';
import { useNavigate } from 'react-router';

import { useProStudioRoutes } from '../../hooks';
import { useProStudioCtxSelector } from '../../xstate/app.xstate';
import { selectInitialTab } from './select-initial-tab';

/** The tabs that are panels of the channel form, on the settings route. */
export type InFormTab = 'settings' | 'pype-theme';
export type ChannelSettingsTab = InFormTab | 'pype-next-theme';

/**
 * Router state carried when a tab switch has to cross a route boundary, so the
 * page being navigated to knows which tab to open. Deliberately narrowed to the
 * in-form tabs: handing `pype-next-theme` to the settings route would select a
 * tab whose panel does not exist there, and the group blanks every panel whose
 * name doesn't match the active tab.
 */
export interface ChannelSettingsTabLocationState {
  channelTab?: InFormTab;
}

const PYPE_NEXT_TAB: ChannelSettingsTab = 'pype-next-theme';

const pressedPanel = (target: EventTarget | null): ChannelSettingsTab | null =>
  ((target as HTMLElement | null)
    ?.closest?.('ps-tab')
    ?.getAttribute('panel') as ChannelSettingsTab | null) ?? null;

/**
 * The Settings / Pype Theme / Pype Next Theme tab bar (PSTUDIO-3768).
 *
 * Settings and Pype Theme are plain panels of one mounted `ChannelSettingsForm`
 * — switching between them changes no URL, because they share a single
 * react-hook-form instance and a single "Save Changes" submit (one save writes
 * branding AND the platform/rule-set assignments). Unmounting either would drop
 * in-flight edits.
 *
 * Pype Next Theme is different: it lives on its own route. Both it and the
 * channel form register a `useUnsavedChanges` guard, and react-router warns and
 * then consults only the LAST blocker registered (`entries[entries.length - 1]`
 * in react-router's data router) — mounting them together would disable one of the two
 * guards. Keeping it a route also avoids booting a real Pype widget inside a
 * `display: none` panel. So its "tab" navigates, and the designer page renders
 * this same bar with `active="pype-next-theme"`.
 */
export const ChannelSettingsTabs: FC<{
  /**
   * Which surface this bar is on — also the tab it opens on. Applied ONCE,
   * imperatively, and deliberately not a controlled `active` prop on the
   * `<Tab>`s: `@lit/react` explicitly does not dirty-check property props
   * ("elements are assumed to do this"), so it re-assigns them from a layout
   * effect on EVERY re-render; since the tab group also writes `active` itself,
   * a controlled prop would snap the bar back on every keystroke in the form.
   */
  active: ChannelSettingsTab;
  /**
   * `<TabPanel>` children for whichever tabs this page renders in place. Omit
   * them to render the bar alone: the Pype Designer page needs its content to
   * be a SIBLING of the bar, because the tab group's wrappers do not pass
   * height through (`:host{display:block}`, no `height: 100%` in the chain), so
   * a full-height flex layout nested in a panel collapses.
   */
  children?: ReactNode;
}> = ({ active, children }) => {
  const navigate = useNavigate();
  const routes = useProStudioRoutes();
  const isPypeNextEnabled = useProStudioCtxSelector(
    (ctx) => ctx.featureFlags?.['pype-next'] ?? false
  );

  const groupRef = useRef<
    (HTMLElement & Partial<Pick<TabGroupWC, 'show' | 'updateComplete'>>) | null
  >(null);
  // Frozen on mount so a later prop change can't re-arm the selection below.
  const initialTabRef = useRef(active);
  // Once the user has picked a tab themselves, stop asserting the initial one.
  const userPickedRef = useRef(false);

  // Re-runs on the flag: see `selectInitialTab` for why once is not enough.
  useLayoutEffect(() => {
    const group = groupRef.current;

    if (!group || userPickedRef.current) {
      return;
    }

    // `failed` means the group's own tab cache was still stale (it syncs on
    // `slotchange`, which is queued behind this layout effect). Retry once that
    // has settled rather than leaving the bar on the wrong tab.
    if (selectInitialTab(group, initialTabRef.current) === 'failed') {
      void group.updateComplete?.then(() => {
        if (!userPickedRef.current) {
          selectInitialTab(group, initialTabRef.current);
        }
      });
    }
  }, [isPypeNextEnabled]);

  /**
   * Cross-route tab presses are handled here, in the CAPTURE phase, so the tab
   * group never sees them.
   *
   * If it did, its `setActiveTab` would run first and deactivate every panel
   * whose name doesn't match the tab just pressed — and that tab's panel lives
   * on the OTHER route, so that means all of them. Normally invisible because
   * the navigation follows immediately; but this form registers a
   * `useUnsavedChanges` blocker, so on a dirty form the navigation is blocked,
   * and a user who answers "Stay" would be left on Channel Settings with the
   * tab bar and nothing under it. Intercepting leaves the group's state alone,
   * so a blocked navigation just keeps showing the current tab.
   */
  const interceptCrossRoute = (e: React.SyntheticEvent) => {
    const pressed = pressedPanel(e.target);

    if (!pressed) {
      return;
    }

    userPickedRef.current = true;

    const crossesRoute =
      active === PYPE_NEXT_TAB
        ? pressed !== PYPE_NEXT_TAB
        : pressed === PYPE_NEXT_TAB;

    if (!crossesRoute) {
      return;
    }

    e.stopPropagation();
    e.preventDefault();

    if (active === PYPE_NEXT_TAB) {
      navigate(routes.channel_settings.path, {
        state: { channelTab: pressed as InFormTab },
      });
      return;
    }

    navigate(routes.pype_designer.path);
  };

  return (
    <TabGroup
      // The wrapper types `ref` as its own props object rather than the element
      // (GeneratePropsWithRef), which the element cannot satisfy (`style`).
      ref={groupRef as RefObject<ElementRef<typeof TabGroup>>}
      test-id="ps-channel-settings-tab-group"
      // Both input paths — the group selects on click AND on Enter/Space.
      // The capture handlers are missing from the wrapper's props type; React
      // binds them regardless, so spread them to sidestep the excess-property check.
      {...{
        onClickCapture: interceptCrossRoute,
        onKeyDownCapture: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            interceptCrossRoute(e);
          }
        },
      }}
    >
      <Tab slot="tab" panel="settings" test-id="ps-channel-settings-tab">
        Settings
      </Tab>
      <Tab slot="tab" panel="pype-theme" test-id="ps-channel-pype-theme-tab">
        Pype Theme
      </Tab>
      {isPypeNextEnabled && (
        <Tab
          slot="tab"
          panel={PYPE_NEXT_TAB}
          test-id="ps-channel-pype-next-theme-tab"
        >
          Pype Next Theme
        </Tab>
      )}
      {/* Each page supplies only the panels it owns; the tab whose content
          lives on the other route has none here, and never becomes active
          because its press is intercepted above. */}
      {children}
    </TabGroup>
  );
};
